import React, { useState, useMemo } from 'react';
import { Booking } from '../../types/booking';
import { bookingService } from '../../services/bookingService';

interface BookingCalendarViewProps {
  bookings: Booking[];
  onBookingSelect?: (booking: Booking) => void;
  onDateSelect?: (date: Date) => void;
}

const BookingCalendarView: React.FC<BookingCalendarViewProps> = ({
  bookings,
  onBookingSelect,
  onDateSelect
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped: { [key: string]: Booking[] } = {};
    
    bookings.forEach(booking => {
      const pickupDate = new Date(booking.pickUpDate);
      const returnDate = new Date(booking.returnDate);
      
      // Add booking to all dates in the range
      const current = new Date(pickupDate);
      while (current <= returnDate) {
        const dateKey = current.toISOString().split('T')[0];
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(booking);
        current.setDate(current.getDate() + 1);
      }
    });
    
    return grouped;
  }, [bookings]);

  const getBookingsForDate = (date: Date) => {
    const dateKey = date.toISOString().split('T')[0];
    return bookingsByDate[dateKey] || [];
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="bg-gray-50 border border-gray-100"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      date.setHours(0, 0, 0, 0);
      const dateBookings = getBookingsForDate(date);
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
      
      const statusCounts: Record<string, number> = {
        confirmed: 0,
        'in-progress': 0,
        completed: 0,
        pending: 0
      };

      dateBookings.forEach(booking => {
        const statusKey = booking.status as string;
        if (statusCounts.hasOwnProperty(statusKey)) {
          statusCounts[statusKey]++;
        }
      });

      days.push(
        <div
          key={day}
          onClick={() => {
            setSelectedDate(date);
            onDateSelect?.(date);
          }}
          className={`min-h-[100px] border border-gray-200 p-2 cursor-pointer transition-all hover:bg-blue-50 ${
            isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'
          } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-semibold ${
              isToday ? 'text-blue-600' : 'text-gray-700'
            }`}>
              {day}
            </span>
            {dateBookings.length > 0 && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                {dateBookings.length}
              </span>
            )}
          </div>
          
          {dateBookings.length > 0 && (
            <div className="space-y-1">
              {statusCounts.confirmed > 0 && (
                <div className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded truncate">
                  {statusCounts.confirmed} confirmed
                </div>
              )}
              {statusCounts['in-progress'] > 0 && (
                <div className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded truncate">
                  {statusCounts['in-progress']} active
                </div>
              )}
              {statusCounts.completed > 0 && (
                <div className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded truncate">
                  {statusCounts.completed} completed
                </div>
              )}
              {statusCounts.pending > 0 && (
                <div className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded truncate">
                  {statusCounts.pending} pending
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const selectedDateBookings = selectedDate ? getBookingsForDate(selectedDate) : [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
            >
              Today
            </button>
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              title="Previous month"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              title="Next month"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px mb-px">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 py-2 text-center text-xs font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {renderCalendarDays()}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && selectedDateBookings.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Bookings on {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedDateBookings.map(booking => (
              <div
                key={booking._id}
                onClick={() => onBookingSelect?.(booking)}
                className="bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {booking.bookingReference}
                    </p>
                    <p className="text-xs text-gray-500">
                      {booking.customer ? `${booking.customer.firstName} ${booking.customer.lastName}` : 'N/A'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    bookingService.getStatusColor(booking.status)
                  }`}>
                    {bookingService.formatStatus(booking.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-gray-600 font-medium">Legend:</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-100 rounded"></div>
            <span className="text-gray-600">Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-purple-100 rounded"></div>
            <span className="text-gray-600">Active</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 rounded"></div>
            <span className="text-gray-600">Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-100 rounded"></div>
            <span className="text-gray-600">Pending</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendarView;
